"use client"

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { apiClient } from "@/lib/apiClient";
import { goToDashboard } from "@/features/sso/goToDashboard";
import { hideKey, ysyncBannerView, type YSyncState } from "@/lib/services/ysyncBanner";

/**
 * YClients sync banner (boona docs/codebase/imports/yclients/full/README.md §8).
 *
 * While the salon is linked to YClients, every page says so: YClients data is
 * copied here every few hours and wins collisions, and Boona sends no client
 * messages. Reads GET /api/v1/company/ysync (Redis-cached on the shard). Not
 * dismissible — it describes how the salon's data behaves right now — except
 * "partial" (a simple import finished), whose ✕ is remembered per salon in this
 * browser (`hideKey`). The switch lives in the dashboard's Settings → YClients.
 */

function storedHidden(key: string | null): boolean {
  if (!key) return false;
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}
export function YSyncBanner() {
  const { t, i18n } = useTranslation();
  const { data } = useQuery<YSyncState>({
    queryKey: ["company-ysync"],
    queryFn: async () => (await apiClient.get("/api/v1/company/ysync")).data,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
    retry: 1,
  });

  const [hiddenNow, setHiddenNow] = useState(false);
  const key = hideKey(data);
  const view = data ? ysyncBannerView(data, t, i18n.language) : null;
  if (!view || hiddenNow || storedHidden(key)) return null;
  const tones = {
    info: "bg-blue-50 text-blue-800 border-blue-200",
    warn: "bg-amber-50 text-amber-800 border-amber-200",
    ok: "bg-emerald-50 text-emerald-800 border-emerald-200",
  };
  const cta = view.cta;

  return (
    <div className={`flex items-center gap-3 border-b px-4 py-2 text-sm ${tones[view.tone]}`} data-testid="ysync-banner">
      <span className="flex-1">{view.message}</span>
      {cta && (
        <button
          type="button"
          onClick={() => {
            if (cta.href) window.location.href = cta.href;
            else void goToDashboard(cta.dashboardPath || "/dashboard/settings/yclients");
          }}
          className="shrink-0 rounded-md border border-current bg-white px-3 py-1 font-semibold"
        >
          {cta.label}
        </button>
      )}
      {key && (
        <button
          type="button"
          aria-label={t("common.close", "Close")}
          title={t("common.close", "Close")}
          onClick={() => {
            try {
              window.localStorage.setItem(key, "1");
            } catch {
              /* private mode: hidden for this visit only */
            }
            setHiddenNow(true);
          }}
          className="shrink-0 rounded-md p-1 opacity-70 hover:opacity-100"
          data-testid="ysync-banner-hide"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
